/**
 * WHICH source the opener's ACT / YES / NO cells are allowed to render, and
 * whether it is still fresh.
 *
 * One resolver for all three cells AND for the ACT-column sort, so a displayed
 * number and the rank it sorts under can never come from different sources.
 *
 * The rules it encodes:
 *  - The venue read is the only ACCOUNT-CORRECT source for a group. The REST
 *    `prediction_positions` map is not account-scoped in this client: it holds
 *    the RESIDUAL account pair's positions. So it may stand in ONLY for a row
 *    whose two accounts ARE that residual pair. Every other row renders `—`.
 *  - A row's accounts are a PAIR, chosen independently per venue: a group may
 *    hold Kalshi on one account and Polymarket on another. A pair with one
 *    half missing names nothing, so it is `unresolved` as a whole.
 *  - The accounts are a property of each ROW's group, not of the page: the
 *    table mixes groups under "All groups", so they are resolved per row.
 *  - Freshness is aged in DURATIONS, never by subtracting a server timestamp
 *    from the browser clock, and the horizon comes off the wire (`staleMs`).
 *  - `ambiguous` counts belong to more than one pair and `null` counts are a
 *    hole in the read: both are "unknown", not a number.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  PairVenuePositions,
  ReadPairVenuePositions,
  VenuePositionsMeta,
  VenuePositionsMetaFrame,
  VenuePositionsSource
} from '../types/entry.types';

/**
 * The wire meta as this module reads it: the two per-venue account ids the feed
 * pod stamps (`''` when the manifest stamped neither — a core pod, or a feed
 * pod predating per-group accounts).
 *
 * Declared here rather than imported because `VenuePositionsMeta` still carries
 * the single `account` discriminator this module no longer has any use for; a
 * real `VenuePositionsMetaFrame` remains assignable to the types below.
 */
/**
 * Aliases kept as the names this module reads by; `entry.types.ts` now declares
 * the wire truth (the two account ids, no `account: 'prod' | 'test'`), so these
 * no longer widen anything.
 */
export type VenueAccountMeta = VenuePositionsMeta;
export type VenueAccountFrame = VenuePositionsMetaFrame;

/**
 * Stale horizon used only when the backend does not send `staleMs`. Matches the
 * backend default (`STALE_MS_DEFAULT`) so an older pod degrades to the same
 * window it would have asked for.
 */
export const VENUE_STALE_MS_FALLBACK = 45_000;

/** Extra delay on the re-render scheduled at the stale boundary. */
const STALE_TICK_GRACE_MS = 250;

/** `setTimeout` truncates anything past this to "fire on the next tick". */
const MAX_TIMEOUT_MS = 2_147_483_647;

/**
 * Delay for the re-render scheduled at the stale boundary.
 *
 * `setTimeout` truncates a delay past 2^31−1 to "fire now", which would re-arm
 * on every tick and SPIN the page instead of degrading it. `venueStaleMs`
 * already caps the horizon, so nothing currently reaches that bound — this is
 * the second lock on the same door, for when the ceiling is raised.
 */
export const staleTickDelayMs = (msLeft: number): number =>
  Math.min(msLeft + STALE_TICK_GRACE_MS, MAX_TIMEOUT_MS);

export type VenueUnavailableReason =
  | 'unconfigured'
  | 'blocked'
  | 'error'
  | 'starting'
  | 'stale'
  | 'missing'
  | 'ambiguous'
  | 'no-read'
  | 'link-down';

/**
 * How long the last venue counts may be HELD while this page has no socket.
 *
 * A dropped socket ages the read into `stale` even though the group's feed pod
 * may be sweeping normally — the heartbeat simply has no way in. On a row where
 * REST may not stand in (any row not on the residual pair, or one whose pair is
 * unresolved) that turned every reconnect into the three cells flipping to `—`
 * and back, which reads as "the position vanished".
 *
 * Holding the group's OWN last counts, visibly marked, never crosses the line
 * the design actually protects: it is not another account's number. But it IS
 * an older one, so it is bounded — past this window "your counts are minutes
 * old" stops being information worth acting on and the cells go dark on the
 * ordinary `stale` path.
 */
export const VENUE_LINK_DOWN_HOLD_MS = 300_000;

/**
 * The two venue accounts a ROW trades, or the fact that we cannot name them.
 *
 * There is no half-resolved variant on purpose. A pair whose Polymarket half is
 * right and whose Kalshi half is stale looks plausible and is worse than an
 * obviously wrong one: it would pass every "do these accounts match" check on
 * the half that happens to agree.
 */
export type RowAccounts =
  | { kind: 'resolved'; kalshiAccountId: string; polymarketAccountId: string }
  | { kind: 'unresolved' };

export const UNRESOLVED_ACCOUNTS: RowAccounts = { kind: 'unresolved' };

/** A blank / absent account id names nothing and is never an id. */
const normalizeAccountId = (id: string | null | undefined): string | null => {
  const trimmed = (id ?? '').trim();
  return trimmed === '' ? null : trimmed;
};

/**
 * Build a pair from two ids, `unresolved` unless BOTH name an account. This is
 * the only constructor: nothing else may mint a `resolved` pair, which is what
 * makes the no-half-resolved rule a property of the type rather than of each
 * call site.
 */
export const accountPair = (
  kalshiAccountId: string | null | undefined,
  polymarketAccountId: string | null | undefined
): RowAccounts => {
  const kalshi = normalizeAccountId(kalshiAccountId);
  const polymarket = normalizeAccountId(polymarketAccountId);
  if (kalshi == null || polymarket == null) return UNRESOLVED_ACCOUNTS;
  return {
    kind: 'resolved',
    kalshiAccountId: kalshi,
    polymarketAccountId: polymarket
  };
};

/** Two pairs are equal only when both are resolved and both halves match. */
export const accountsEqual = (a: RowAccounts, b: RowAccounts): boolean => {
  if (a.kind !== 'resolved' || b.kind !== 'resolved') {
    return a.kind === b.kind;
  }
  return (
    a.kalshiAccountId === b.kalshiAccountId &&
    a.polymarketAccountId === b.polymarketAccountId
  );
};

/**
 * Cache/identity key for a pair. Account ids are DNS-label slugs (lowercase
 * letters, digits, dashes), so `|` cannot occur inside one and the two halves
 * stay unambiguous.
 */
export const accountsKey = (accounts: RowAccounts): string =>
  accounts.kind === 'resolved'
    ? `resolved:${accounts.kalshiAccountId}|${accounts.polymarketAccountId}`
    : 'unresolved';

/** Human-readable "which book" — the operator's next question on every cell. */
export const accountsLabel = (accounts: RowAccounts): string =>
  accounts.kind === 'resolved'
    ? `Kalshi ${accounts.kalshiAccountId} / Polymarket ${accounts.polymarketAccountId}`
    : 'unresolved accounts';

/** Everything the three cells render about the group-level venue read. */
export interface VenueViewStatus {
  /** The per-pair venue counts may be rendered. */
  fresh: boolean;
  /** The residual-account REST numbers may stand in for this view. */
  restAllowed: boolean;
  /**
   * WHOSE numbers this view is about — the pair the pod stamped when it sent
   * one, otherwise the row's own. What the tooltips name.
   */
  accounts: RowAccounts;
  source: VenuePositionsSource | 'none';
  /**
   * Why the venue counts are not being rendered. Advisory in the two cases
   * where something IS rendered: with `restAllowed` it names what the REST
   * numbers are standing IN FOR (`stale` = this group's own feed pod stopped
   * sweeping), and `link-down` marks counts that ARE this group's own but are
   * being held while the page has no socket.
   */
  reason: VenueUnavailableReason | null;
  lastError: string | null;
  redeemableExcluded: number;
  /** ISO of the last successful sweep — set only while NOT fresh, where it is
   *  the one thing the unavailable (or stood-in-for) tooltip shows. Keeping the
   *  raw advancing timestamp out of the status is what makes its identity
   *  stable across the per-poll heartbeat. */
  lastSuccessLabel: string | null;
}

/**
 * Upper bound on the horizon we will honour off the wire. The backend clamps
 * `staleMs` too, so this is defence in depth against an older or misconfigured
 * pod: an unbounded horizon means the counts NEVER go stale, which is the same
 * money-losing direction as a frozen clock — a dead feed rendering as live.
 */
export const VENUE_STALE_MS_CEILING = 1_800_000;

export const venueStaleMs = (meta: VenueAccountMeta): number =>
  typeof meta.staleMs === 'number' && meta.staleMs > 0
    ? Math.min(meta.staleMs, VENUE_STALE_MS_CEILING)
    : VENUE_STALE_MS_FALLBACK;

/**
 * Age of the last successful sweep, computed WITHOUT comparing the two clocks:
 * `serverEmittedAt − lastSuccessAt` is entirely in the server clock and the
 * time since the frame landed entirely in the browser's, so any constant skew
 * cancels.
 *
 * The elapsed-since-receipt term is read off the MONOTONIC clock
 * (`performance.now()`, `receivedMono`) rather than the wall clock: a wall
 * clock corrected BACKWARDS after the frame arrived makes `now - receivedAt`
 * negative, and clamping that at 0 would freeze the age — the counts would
 * read fresh for as long as the correction lasts even with a dead feed. A
 * monotonic clock cannot be corrected. `receivedMono` is absent only on a
 * frame built before this field existed, where the clamped wall-clock
 * difference stands in.
 *
 * Without `serverEmittedAt` (backend older than the `_meta` envelope) the first
 * term does mix clocks, but it is clamped at 0 — so a browser running behind
 * the pod can only lose the age accrued BEFORE the frame arrived, never keep a
 * dead feed looking fresh: the elapsed term grows on its own once the
 * heartbeat stops.
 */
export const venueAgeMs = (
  frame: VenueAccountFrame,
  now: number = Date.now(),
  mono: number = performance.now()
): number => {
  if (frame.meta.lastSuccessAt == null) return Number.POSITIVE_INFINITY;
  const anchor = frame.serverEmittedAt ?? frame.receivedAt;
  const ageAtFrame = Math.max(0, anchor - frame.meta.lastSuccessAt);
  const sinceFrame =
    frame.receivedMono == null
      ? Math.max(0, now - frame.receivedAt)
      : Math.max(0, mono - frame.receivedMono);
  return ageAtFrame + sinceFrame;
};

/** ms until the current read goes stale; `null` when there is nothing ageing. */
export const msUntilVenueStale = (
  frame: VenueAccountFrame | null | undefined,
  now: number = Date.now(),
  mono: number = performance.now()
): number | null => {
  if (!frame || frame.meta.source !== 'venue') return null;
  const age = venueAgeMs(frame, now, mono);
  if (!Number.isFinite(age)) return null;
  return venueStaleMs(frame.meta) - age;
};

/**
 * ms until the next moment the cells would render differently: the stale
 * boundary, or — once past it with the socket down — the end of the offline
 * hold. `null` when nothing is ageing towards a change.
 *
 * The hold boundary needs its own re-render for the same reason the stale one
 * does: nothing else schedules a render, so held counts would otherwise stay on
 * screen past the window that bounds them.
 */
export const msUntilVenueBoundary = (
  frame: VenueAccountFrame | null | undefined,
  linkDown = false,
  now: number = Date.now(),
  mono: number = performance.now()
): number | null => {
  if (!frame) return null;
  const untilStale = msUntilVenueStale(frame, now, mono);
  if (untilStale == null) return null;
  if (untilStale > 0) return untilStale;
  if (!linkDown) return null;
  const untilHoldEnds = VENUE_LINK_DOWN_HOLD_MS - venueAgeMs(frame, now, mono);
  return untilHoldEnds > 0 ? untilHoldEnds : null;
};

const isoOrNull = (at: number | null | undefined): string | null =>
  at == null ? null : new Date(at).toISOString();

/** True when the pod named an account this row is not believed to trade. */
const podContradicts = (
  accounts: RowAccounts,
  meta: VenueAccountMeta | null
): boolean => {
  if (!meta || accounts.kind !== 'resolved') return false;
  const kalshi = normalizeAccountId(meta.kalshiAccountId);
  const polymarket = normalizeAccountId(meta.polyAccountId);
  return (
    (kalshi != null && kalshi !== accounts.kalshiAccountId) ||
    (polymarket != null && polymarket !== accounts.polymarketAccountId)
  );
};

/**
 * The REST `prediction_positions` map this page holds is NOT account-scoped:
 * it is the RESIDUAL pair's positions and nothing narrows it further. So it may
 * stand in ONLY for a row whose two accounts ARE that residual pair, and only
 * while the pod has not reported different ones.
 *
 * This is deliberately conservative and MUST stay that way. Widening it to any
 * resolved pair renders the residual account's numbers as the position of a
 * group trading somewhere else — the exact fabrication this module exists to
 * prevent, and one the operator cannot tell from a real read.
 */
const restAccountProven = (
  accounts: RowAccounts,
  residual: RowAccounts,
  meta: VenueAccountMeta | null
): boolean =>
  // Both must be RESOLVED, not merely equal: two unresolved pairs ARE equal
  // (`accountsEqual` is structural, which is what status identity needs), and
  // "we don't know either" is not a proof of anything.
  accounts.kind === 'resolved' &&
  residual.kind === 'resolved' &&
  accountsEqual(accounts, residual) &&
  !podContradicts(accounts, meta);

/**
 * WHOSE numbers a view is about. The pod's stamp wins where it exists — it read
 * the venue, so it knows — and a HALF stamp names no pair at all.
 */
const viewAccounts = (
  accounts: RowAccounts,
  meta: VenueAccountMeta | null
): RowAccounts => {
  if (!meta) return accounts;
  const kalshi = normalizeAccountId(meta.kalshiAccountId);
  const polymarket = normalizeAccountId(meta.polyAccountId);
  if (kalshi == null && polymarket == null) return accounts;
  return accountPair(kalshi, polymarket);
};

export function resolveVenueStatus(
  frame: VenueAccountFrame | null | undefined,
  accounts: RowAccounts,
  residual: RowAccounts,
  now: number = Date.now(),
  mono: number = performance.now(),
  linkDown = false
): VenueViewStatus {
  const meta = frame?.meta ?? null;
  const base = {
    accounts: viewAccounts(accounts, meta),
    source: meta?.source ?? ('none' as const),
    lastError: meta?.lastError ?? null,
    redeemableExcluded: meta?.redeemableExcluded ?? 0
  };

  if (meta && frame) {
    if (meta.source === 'venue') {
      const age = venueAgeMs(frame, now, mono);
      const stale = age > venueStaleMs(meta);
      // A stale venue read on a row PROVEN to be on the residual pair degrades
      // to the residual REST number, which is the right answer for that row —
      // only a row on other accounts (or an unresolved one) has to go dark,
      // because there REST is a different account's position.
      // The degrade still REPORTS `stale`: it is the one signal that this
      // group's own feed pod stopped sweeping, and dropping it leaves an
      // ordinary REST cell with nothing to say so.
      const degradeToRest = stale && restAccountProven(accounts, residual, meta);
      // Nothing above can tell "the pod stopped sweeping" from "we lost the
      // socket carrying its heartbeat" — but the page CAN: the socket is its
      // own. While it is down, and only where there is no REST to fall back
      // on, the group's own last counts are HELD (marked, and bounded by
      // VENUE_LINK_DOWN_HOLD_MS) rather than blanked on every reconnect.
      if (stale && !degradeToRest && linkDown && age <= VENUE_LINK_DOWN_HOLD_MS) {
        return {
          ...base,
          fresh: true,
          restAllowed: false,
          reason: 'link-down',
          lastSuccessLabel: isoOrNull(meta.lastSuccessAt)
        };
      }
      return {
        ...base,
        fresh: !stale,
        restAllowed: degradeToRest,
        reason: stale ? 'stale' : null,
        lastSuccessLabel: stale ? isoOrNull(meta.lastSuccessAt) : null
      };
    }
    // Any other source is a pod that HAS a venue to read and did not read it —
    // `unconfigured` / `blocked` / `error`, and `starting` (an armed feed pod
    // before its first successful sweep). None of them may borrow the REST
    // numbers, on any accounts: a feed pod's group is not necessarily on the
    // residual pair, and a group still coming up would otherwise show the
    // residual account's position as its own for the whole boot window.
    if (meta.source !== 'off') {
      return {
        ...base,
        fresh: false,
        restAllowed: false,
        reason: meta.source,
        lastSuccessLabel: isoOrNull(meta.lastSuccessAt)
      };
    }
  }

  // No meta at all (core gateway), or a pod reporting `off` — the CORE pod,
  // which holds no venue credentials at all, so the residual REST map is the
  // legitimate answer there for a row actually on the residual pair.
  const proven = restAccountProven(accounts, residual, meta);
  return {
    ...base,
    fresh: false,
    restAllowed: proven,
    reason: proven ? null : 'no-read',
    lastSuccessLabel: proven ? null : isoOrNull(meta?.lastSuccessAt)
  };
}

/**
 * A row on the residual pair whose own venue read went stale falls back to the
 * REST numbers, which is the account-correct answer — but this note is the only
 * place the operator would ever learn that the group's feed pod STOPPED
 * SWEEPING, so the cells say so instead of passing for an ordinary REST read.
 */
export const restStaleVenueNote = (status: VenueViewStatus): string =>
  status.restAllowed && status.reason === 'stale'
    ? ' · This group’s own venue read is STALE (last success ' +
      `${status.lastSuccessLabel ?? 'unknown'}) — its feed pod stopped ` +
      `sweeping, so these are the REST numbers for ${accountsLabel(
        status.accounts
      )} standing in.`
    : '';

/** Field-by-field equality over everything the cells render. */
export const venueStatusEqual = (
  a: VenueViewStatus,
  b: VenueViewStatus
): boolean =>
  a.fresh === b.fresh &&
  a.restAllowed === b.restAllowed &&
  accountsEqual(a.accounts, b.accounts) &&
  a.source === b.source &&
  a.reason === b.reason &&
  a.lastError === b.lastError &&
  a.redeemableExcluded === b.redeemableExcluded &&
  a.lastSuccessLabel === b.lastSuccessLabel;

export type PairVenueView =
  | {
      kind: 'venue';
      venue: ReadPairVenuePositions;
      accounts: RowAccounts;
      resolving: boolean;
    }
  | { kind: 'rest' }
  | { kind: 'unavailable'; reason: VenueUnavailableReason };

/**
 * A count that is not a finite number is a hole in the read, not a position:
 * rendering it would print `NaN`, and ranking it would make the ACT
 * comparator inconsistent (`NaN - x` is `NaN`, so the sort order becomes
 * implementation-defined).
 */
const countsUsable = (v: PairVenuePositions): v is ReadPairVenuePositions =>
  Number.isFinite(v.polyYes) &&
  Number.isFinite(v.polyNo) &&
  Number.isFinite(v.kalshiYes) &&
  Number.isFinite(v.kalshiNo);

/** The source ONE row may render, given the group status and its own counts. */
export function resolvePairVenueView(
  status: VenueViewStatus,
  venue: PairVenuePositions | null | undefined
): PairVenueView {
  if (status.fresh) {
    if (!venue) return { kind: 'unavailable', reason: 'missing' };
    if (venue.ambiguous) return { kind: 'unavailable', reason: 'ambiguous' };
    if (!countsUsable(venue)) return { kind: 'unavailable', reason: 'missing' };
    return {
      kind: 'venue',
      venue,
      accounts: status.accounts,
      resolving: venue.resolving === true
    };
  }
  if (status.restAllowed) return { kind: 'rest' };
  return { kind: 'unavailable', reason: status.reason ?? 'no-read' };
}

/**
 * Signed `YES − NO` from the venue-polled per-market counts, same excess-YES
 * convention as the REST and believed-position nets.
 */
export const venueSignedNet = (v: ReadPairVenuePositions): number =>
  v.polyYes + v.kalshiYes - (v.polyNo + v.kalshiNo);

/**
 * Sort magnitude for the ACT column, taken from the SAME view the cell renders
 * — so a row can never be ranked by a source it is not displaying. `Infinity`
 * means "unknown", so those rows lead the descending (largest-naked-first)
 * order instead of tying with the flat majority.
 *
 * The one deliberate divergence between rank and display: a `resolving` pair
 * SHOWS its signed net but RANKS flat, because that imbalance is a settled leg
 * awaiting redemption, not an open naked leg to hedge. It is the same number
 * either way, read for two different questions.
 */
export const venueSortMagnitude = (
  view: PairVenueView,
  restMagnitude: number
): number => {
  if (view.kind === 'venue') {
    if (view.resolving) return 0;
    const mag = Math.abs(venueSignedNet(view.venue));
    return Number.isFinite(mag) ? mag : Number.POSITIVE_INFINITY;
  }
  if (view.kind === 'rest') {
    return Number.isFinite(restMagnitude)
      ? restMagnitude
      : Number.POSITIVE_INFINITY;
  }
  return Number.POSITIVE_INFINITY;
};

/**
 * Re-render this component when the current read crosses its stale boundary,
 * and hand back the tick so the resolver it feeds is recomputed with it.
 *
 * Staleness is otherwise only ever evaluated during a render nothing schedules,
 * so a table that stopped receiving store notifications would keep showing
 * venue counts past the horizon forever.
 */
function useVenueStaleTick(
  frame: VenueAccountFrame | null | undefined,
  linkDown: boolean
): number {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const left = msUntilVenueBoundary(frame, linkDown);
    if (left == null || !Number.isFinite(left) || left <= 0) return;
    const timer = setTimeout(() => setTick(n => n + 1), staleTickDelayMs(left));
    return () => clearTimeout(timer);
  }, [frame, linkDown, tick]);

  // Browsers throttle a hidden tab's timers to ~1/min, so the boundary
  // re-render can arrive minutes late — long enough for a tab left open on a
  // dead feed to still be showing venue counts when the operator returns to
  // it. Re-evaluate on the way back in.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onVisibilityChange = () => {
      if (!document.hidden) setTick(n => n + 1);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  return tick;
}

/**
 * Hold ONE object per distinct status PER account pair: a new identity only when
 * something the cells render changes, so the per-poll heartbeat
 * (`lastSuccessAt` advances every ~10s) no longer re-sorts the table and
 * re-renders every row.
 */
function useStableVenueStatuses(): (
  key: string,
  next: VenueViewStatus
) => VenueViewStatus {
  const held = useRef(new Map<string, VenueViewStatus>());
  return useCallback((key: string, next: VenueViewStatus) => {
    const prev = held.current.get(key);
    if (prev && venueStatusEqual(prev, next)) return prev;
    held.current.set(key, next);
    return next;
  }, []);
}

/** The group venue status for ONE row's account pair. */
export type VenueStatusResolver = (accounts: RowAccounts) => VenueViewStatus;

/**
 * One identity-stable status per distinct account pair, off one frame.
 *
 * Resolved per PAIR rather than once per page because the table is not
 * single-account: under "All groups" it mixes rows from groups on different
 * venue accounts, and the answer to "may this row show a REST number" differs
 * between them. Each row then asks for the status of its OWN pair.
 *
 * `residual` is the pair the REST `prediction_positions` map belongs to, and is
 * passed in — never assumed. Callers must keep its identity stable (memoize
 * it), because a new object re-mints the resolver.
 */
export function useVenueStatusByAccounts(
  frame: VenueAccountFrame | null | undefined,
  residual: RowAccounts,
  linkDown = false
): VenueStatusResolver {
  const tick = useVenueStaleTick(frame, linkDown);
  const stabilize = useStableVenueStatuses();
  return useMemo(() => {
    // Captured once per resolver, so every row in one render judges freshness
    // against the same instant. The boundary timer and the visibility handler
    // above are what re-mint it — nothing else needs to.
    const now = Date.now();
    const mono = performance.now();
    return (accounts: RowAccounts) =>
      stabilize(
        accountsKey(accounts),
        resolveVenueStatus(frame, accounts, residual, now, mono, linkDown)
      );
    // `tick` is a dependency for its side effect: it is how the stale boundary
    // and a returning background tab force a fresh `now`.
  }, [frame, residual, linkDown, tick, stabilize]);
}

/** The fields of an opener group the account pair is derived from. */
export interface GroupAccountRow {
  _id: string;
  kalshiAccountId?: string | null;
  polymarketAccountId?: string | null;
}

/**
 * A group list together with the moment its fetch was ISSUED — the cached
 * `opener-groups` value (`OpenerGroupList`) satisfies this. Taken as one value
 * so the rows and the clock they are judged by cannot come from two different
 * fetches.
 */
export interface GroupAccountSource {
  groups: GroupAccountRow[];
  fetchStartedAt: number;
}

/** Resolved account pair per opener group; absent = not resolved. */
export interface GroupAccountIndex {
  byGroup: ReadonlyMap<string, RowAccounts>;
}

/**
 * Which two accounts each opener group trades, as far as the page can PROVE.
 *
 * The pair may be chosen at START time and is only readable from the
 * `opener-groups` row, which polls every 30s — so a group started against
 * different accounts keeps reporting the PREVIOUS run's pair until that row is
 * re-read. `unresolvedMarks` carries the moment each group's pair was last
 * invalidated (a start/stop); until the group list has been fetched AFTER that
 * moment the group resolves to `unresolved` — for the WHOLE pair, never for
 * only the half we have doubts about.
 *
 * The clock compared against the mark is when the HELD list's fetch STARTED,
 * never when it completed: a fetch already in flight when the mark is written
 * carries pre-start rows, and completing it later would clear the guard with
 * exactly the answer the mark exists to distrust.
 *
 * Equal timestamps are treated as unresolved. Both clocks are `Date.now()`, so
 * one millisecond cannot order them, and a fetch that left within the mark's
 * millisecond is as likely to predate it as to follow it. Staying unresolved for
 * one more poll costs a dark cell; guessing costs the wrong account's numbers.
 */
export function buildGroupAccountIndex(
  list: GroupAccountSource | null | undefined,
  unresolvedMarks: ReadonlyMap<string, number>
): GroupAccountIndex {
  const byGroup = new Map<string, RowAccounts>();
  if (!list) return { byGroup };
  for (const group of list.groups) {
    const markedAt = unresolvedMarks.get(group._id);
    if (markedAt != null && list.fetchStartedAt <= markedAt) continue;
    byGroup.set(
      group._id,
      accountPair(group.kalshiAccountId, group.polymarketAccountId)
    );
  }
  return { byGroup };
}

/**
 * The account pair of ONE row, from the pair's own group — NOT from the page
 * selection, which cannot express "this row's group is on other accounts" while
 * the table is showing All groups / Ungrouped.
 *
 * A pair in no group is traded by the always-on core engine, which runs on the
 * RESIDUAL pair — so that is the answer, and it is passed in rather than
 * assumed. With no residual configured an ungrouped row is `unresolved`: the
 * core engine's accounts are then simply not something this page knows.
 */
export function resolveRowAccounts(
  openerGroupId: string | null | undefined,
  index: GroupAccountIndex,
  residual: RowAccounts
): RowAccounts {
  if (!openerGroupId) return residual;
  return index.byGroup.get(openerGroupId) ?? UNRESOLVED_ACCOUNTS;
}
